import { z } from "zod";
import { baseSpecShape, registerShell } from "./base.js";
/**
 * The fourth shell, added after pick/order/sort to prove the plugin path: this
 * file plus a renderer registered under the same id on the client is the whole
 * cost of a new check type. Nothing in the pipeline, the prompt, the validator
 * or the published JSON Schema needed editing to accept it.
 */
export const matchSpecSchema = z.object({
    type: z.literal("match"),
    ...baseSpecShape,
    pairs: z
        .array(z.object({
        left: z.string().min(1).max(32),
        right: z.string().min(1).max(32),
    }))
        .min(3)
        .max(5),
});
export const matchShell = registerShell({
    id: "match",
    version: 1,
    summary: "Pair each item on the left with its partner on the right.",
    chooseWhen: "The idea is a set of one-to-one relationships — animal to home, word to meaning, planet to fact.",
    interaction: "matches each thing to its partner",
    invitation: "Pairs to find — tap two that belong together.",
    schema: matchSpecSchema,
    required: ["pairs"],
    properties: {
        pairs: {
            type: "array",
            minItems: 3,
            maxItems: 5,
            description: "Each left belongs to exactly one right. The client shuffles the right column for display.",
            items: {
                type: "object",
                required: ["left", "right"],
                properties: {
                    left: { type: "string", maxLength: 32 },
                    right: { type: "string", maxLength: 32 },
                },
            },
        },
    },
    semanticIssues(spec) {
        const issues = [];
        const lefts = new Set(spec.pairs.map((p) => p.left.trim().toLowerCase()));
        const rights = new Set(spec.pairs.map((p) => p.right.trim().toLowerCase()));
        if (lefts.size !== spec.pairs.length)
            issues.push("match left items must all be different.");
        if (rights.size !== spec.pairs.length)
            issues.push("match right items must all be different.");
        // A pair whose two halves read the same gives the answer away for free.
        for (const p of spec.pairs) {
            if (p.left.trim().toLowerCase() === p.right.trim().toLowerCase()) {
                issues.push(`match pair "${p.left}" has the same text on both sides.`);
            }
        }
        return issues;
    },
    example: {
        type: "match",
        prompt: "Match each animal to where it sleeps.",
        pairs: [
            { left: "Bat", right: "Hanging in a cave" },
            { left: "Bear", right: "A den" },
            { left: "Bee", right: "A hive" },
        ],
        right: "Nice matching! Every animal has a place that keeps it safe.",
        wrong: "Not quite — a bat sleeps upside down in a cave.",
    },
});
//# sourceMappingURL=match.js.map